import React, { useMemo, useState } from "react";
import {
  Box,
  ExpandableSection,
  RadioGroup,
  SpaceBetween,
  Spinner,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import { formatDateTimeCell } from "./DateTimeCellEditor";

/**
 * Renders the "required when triggered" DATE columns for a service, grouped by
 * the Task Manager dashboards its app belongs to. One ExpandableSection per
 * dashboard, each with a single-select RadioGroup of its DATE columns. The
 * uploader must pick one option in every dashboard group before submitting
 * (mirrors the SEC Central corp-reporting Pre/Post WB radio), so the parent
 * gates Submit on completeness.
 *
 * The data is fetched by the parent page (in the background, while file
 * validation runs) and passed in via `dashboards`; this component is purely
 * presentational so the fetch can start before the component renders.
 *
 * Props:
 *   dashboards: [ { dashboard_name, dashboard_id,
 *                   date_columns: [ { name, due_date_type, value } ] } ] | null
 *   loading:    boolean  - fetch in flight
 *   error:      string   - fetch error message (optional)
 *   onSelectionChange: (selection) => void  - called with the transaction-record
 *                   shape { "<dashboard_id>": { dates: [dueDateType], value } }
 *                   whenever a radio changes.
 */
const RequiredDateColumns = ({
  dashboards,
  loading = false,
  error = "",
  onSelectionChange,
}) => {
  // Selected column NAME per dashboard: { [dashboard_id]: name }.
  const [selected, setSelected] = useState({});

  // A date_columns entry is either the new object shape { name, due_date_type,
  // value } or (legacy) a bare column-name string. Normalize all three fields:
  // `name` is shown, `dateType` is the tracking sub-group the transaction carries
  // (the grid's DATE status column reads by due_date_type, not name), and `value`
  // is the admin-set datetime (epoch-ms string) shown next to the name.
  const columnName = (col) => (typeof col === "string" ? col : col?.name);
  const columnDateType = (col) =>
    typeof col === "string" ? col : col?.due_date_type || col?.name;
  const columnValue = (col) => (typeof col === "string" ? "" : col?.value || "");

  // Only show dashboards that actually have required DATE columns.
  const dashboardsWithColumns = useMemo(
    () =>
      (Array.isArray(dashboards) ? dashboards : []).filter(
        (d) => Array.isArray(d?.date_columns) && d.date_columns.length > 0
      ),
    [dashboards]
  );

  // Reduce the { dashboard_id: name } picks into the transaction-record shape:
  //   { "<dashboard_id>": { dates: [dueDateType], value }, ... }
  // (only dashboards with a pick). `dates` carries the due_date_type, matching
  // what the dashboard grid's DATE status column reads.
  const toSelection = (pickMap) => {
    const byDashboard = {};
    Object.entries(pickMap).forEach(([dashboardId, name]) => {
      if (!name) return;
      const dash = dashboardsWithColumns.find(
        (d) => String(d.dashboard_id) === String(dashboardId)
      );
      const col = (dash?.date_columns || []).find((c) => columnName(c) === name);
      const dateType = col ? columnDateType(col) : name;
      byDashboard[dashboardId] = { dates: [dateType], value: col ? columnValue(col) : "" };
    });
    return byDashboard;
  };

  const handleSelect = (dashboard, name) => {
    const next = { ...selected, [dashboard.dashboard_id]: name };
    setSelected(next);
    if (typeof onSelectionChange === "function") {
      onSelectionChange(toSelection(next));
    }
  };

  if (loading) {
    return (
      <Box padding={{ top: "xs" }}>
        <Spinner size="normal" /> Loading required date fields...
      </Box>
    );
  }

  if (error) {
    return (
      <Box padding={{ top: "xs" }}>
        <StatusIndicator type="warning">
          Could not load required date fields: {error}
        </StatusIndicator>
      </Box>
    );
  }

  if (dashboardsWithColumns.length === 0) {
    // Before a fetch has run (dashboards === null) render nothing. After a fetch
    // that returned no required DATE columns, surface a hint instead of silently
    // rendering nothing, so it's clear the section loaded but had no data.
    if (dashboards == null) return null;
    return (
      <Box padding={{ top: "xs" }} color="text-status-inactive">
        No required date fields for this deliverable.
      </Box>
    );
  }

  const allSelected = dashboardsWithColumns.every(
    (d) => selected[d.dashboard_id]
  );

  return (
    <SpaceBetween size="xs">
      <Box variant="h5">Required date fields</Box>
      {!allSelected && (
        <StatusIndicator type="warning">
          Select a date field in each section before submitting.
        </StatusIndicator>
      )}
      {dashboardsWithColumns.map((dashboard) => {
        const items = dashboard.date_columns.map((col) => {
          const name = columnName(col);
          const value = columnValue(col);
          return {
            value: name,
            label: value ? `${name} — ${formatDateTimeCell(value)}` : name,
          };
        });
        return (
          <ExpandableSection
            key={dashboard.dashboard_id}
            defaultExpanded
            headerText={dashboard.dashboard_name || `Dashboard ${dashboard.dashboard_id}`}
          >
            <RadioGroup
              value={selected[dashboard.dashboard_id] || null}
              items={items}
              onChange={({ detail }) => handleSelect(dashboard, detail.value)}
            />
          </ExpandableSection>
        );
      })}
    </SpaceBetween>
  );
};

export default RequiredDateColumns;
